import { useLocale } from '../i18n/LocaleContext';
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const features = [
  {
    id: 1,
    icon: '📝'
  },
  {
    id: 2,
    icon: '⏱️'
  },
  {
    id: 3,
    icon: '🔒'
  },
  {
    id: 4,
    icon: '☝️'
  },
  {
    id: 5,
    icon: '🤗️'
  },
  {
    id: 6,
    icon: '🤝'
  }
];

const Features = () => {
  const { t, path } = useLocale();
  return (
    <FeaturesSection>
      <div className="container">
        <SectionTitle>{t('features.title')}</SectionTitle>
        <SectionSubtitle>
          {t('features.description')}
        </SectionSubtitle>

        <FeatureGrid>
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.id}
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{t(`features.${feature.id}.title`)}</FeatureTitle>
              <FeatureDescription>{t(`features.${feature.id}.description`)}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeatureGrid>

        <LearnMoreContainer
          as={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <LearnMoreLink to={path("/how-to-use")}>{t('features.learn')}</LearnMoreLink>
        </LearnMoreContainer>
      </div>
    </FeaturesSection>
  );
};

const FeaturesSection = styled.section`
  padding: 100px 0;
  background-color: var(--white);
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 20px;
  font-weight: 700;
  color: var(--primary);
  text-align: center;
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 60px;
  max-width: 800px;
  margin-inline-start: auto;
  margin-inline-end: auto;
  text-align: center;
  color: var(--secondary);
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 20px;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: var(--primary-dark);
`;

const FeatureDescription = styled.p`
  color: var(--dark-gray);
  line-height: 1.6;
`;

const LearnMoreContainer = styled.div`
  margin-top: 50px;
  text-align: center;
`;

const LearnMoreLink = styled(Link)`
  display: inline-block;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--primary);
  padding: 12px 24px;
  border: 2px solid var(--primary);
  border-radius: 8px;
  transition: var(--transition);

  &:hover {
    background-color: var(--primary);
    color: white;
    transform: translateY(-3px);
    box-shadow: var(--shadow);
  }
`;

export default Features;
